<template>
  <nav class="section-nav" aria-label="Sektioner">
    <template v-for="section in sections" :key="section.id">
      <a
        :href="`#${section.id}`"
        :aria-current="activeSection === section.id ? 'location' : undefined"
        class="nav-link rounded-md px-3 py-1.5 transition-colors"
        :class="activeSection === section.id ? 'active font-semibold' : ''"
        @click="handleNavigationClick($event, section.id, section.id)"
      >
        {{ section.title }}
      </a>

      <template v-if="section.subSections?.length && activeSection === section.id">
        <a
          v-for="sub in section.subSections"
          :key="sub.id"
          :href="`#${sub.id}`"
          class="sub-link rounded-md py-1 pl-6 pr-3 text-xs transition-colors"
          @click="handleNavigationClick($event, sub.id, section.id)"
        >
          {{ sub.title }}
        </a>
      </template>
    </template>
  </nav>
</template>

<script setup lang="ts">
import type { NavigationSection } from './sections/sections'

defineProps<{
  activeSection: string
  sections: NavigationSection[]
}>()

const emit = defineEmits<{
  navigate: [sectionId: string]
}>()

const isModifiedEvent = (event: MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

const updateHash = (id: string) => {
  if (window.location.hash === `#${id}`) return
  window.history.pushState(null, '', `#${id}`)
}

const handleNavigationClick = (event: MouseEvent, id: string, sectionId: string) => {
  if (isModifiedEvent(event)) return

  event.preventDefault()
  scrollToTarget(id, sectionId)
}

const scrollToTarget = (id: string, sectionId: string) => {
  emit('navigate', sectionId)
  updateHash(id)
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth' })
}
</script>

<style scoped>
.section-nav {
  position: sticky;
  top: var(--header-height, 0);
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-link {
  color: var(--color-fg-subtle);
}

.nav-link:not(.active):hover {
  background-color: var(--color-canvas-subtle);
  color: var(--color-fg-default);
}

.nav-link.active {
  background-color: var(--color-brand-subtle);
  color: var(--color-brand-emphasis);
}

.sub-link {
  color: var(--color-brand-default);
}

@media (max-width: 767px) {
  .section-nav {
    position: static;
  }
}
</style>
